import { useId, useRef, useState, type SyntheticEvent } from 'react';

import { Button } from '../components/Button';
import { Field, inputClass } from '../components/Field';
import { useToast } from '../components/Toast';
import { useAuth } from '../features/auth/AuthProvider';
import { fieldMessages } from '../features/users/api';
import { ApiError } from '../lib/api';
import { useFocusTrap } from '../lib/useFocusTrap';

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { request } = useAuth();
  const { notify } = useToast();
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(ref, onClose);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: SyntheticEvent) => {
    e.preventDefault();
    setFormError(null);
    if (next !== confirmation) {
      setErrors({ confirmation: 'As senhas não coincidem.' });
      return;
    }
    setErrors({});
    setBusy(true);
    try {
      await request('POST', '/api/v1/auth/password', {
        current_password: current,
        new_password: next,
      });
      notify('Senha alterada. As outras sessões foram encerradas.');
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) setErrors(fieldMessages(err.fields));
      else setFormError('Não foi possível alterar a senha. Tente novamente.');
      setBusy(false);
    }
  };

  // Render functions for Field (called as functions, not rendered as components).
  const input =
    (value: string, set: (v: string) => void, autoComplete: string, autofocus = false) =>
    ({
      id,
      describedBy,
      invalid,
    }: {
      id: string;
      describedBy: string | undefined;
      invalid: boolean;
    }) => (
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => {
          set(e.target.value);
        }}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        {...(autofocus ? { 'data-autofocus': true } : {})}
        className={inputClass}
      />
    );

  return (
    <div className="fixed inset-0 z-50 grid place-items-center px-4">
      <div aria-hidden="true" className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-sm rounded-lg border border-line-strong bg-surface-raised p-5 shadow-2xl shadow-black/50"
      >
        <h2 id={titleId} className="text-sm font-semibold text-fg">
          Alterar senha
        </h2>
        <p className="mt-1 text-xs text-fg-muted">
          As suas outras sessões serão encerradas; esta continua ativa.
        </p>
        <form
          className="mt-4 space-y-3.5"
          noValidate
          onSubmit={(e) => {
            void submit(e);
          }}
        >
          {formError && (
            <p role="alert" className="text-xs text-crit">
              {formError}
            </p>
          )}
          <Field label="Senha atual" error={errors.current_password}>
            {input(current, setCurrent, 'current-password', true)}
          </Field>
          <Field label="Nova senha" hint="Mínimo de 12 caracteres." error={errors.new_password}>
            {input(next, setNext, 'new-password')}
          </Field>
          <Field label="Confirmar nova senha" error={errors.confirmation}>
            {input(confirmation, setConfirmation, 'new-password')}
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={onClose}>Cancelar</Button>
            <Button
              variant="primary"
              type="submit"
              disabled={busy || !current || !next || !confirmation}
            >
              {busy ? 'Salvando…' : 'Alterar senha'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
